/**
 * Ads Manager table footer — the arithmetic of every Total row.
 *
 * ONE function (`totalValue`) derives every footer column from summed
 * numbers, and every footer row goes through it: «Total filtered»,
 * «Total by <App>», and — with «Split by devices» — «Total for iPhone» /
 * «Total for iPad». A device total therefore can't be computed by other
 * formulas than the row above it; there is no second copy to drift.
 *
 * Ratios (CR / CPA / ROAS / ROI / ARPU / TTR / CPT / CPM / IPM) are NEVER
 * summed or averaged across rows — an average of per-row ratios is a
 * different, misleading number. They are recomputed from the sums:
 *   CR      = sum(event_count) / sum(installs)
 *   CPA     = sum(spend) / sum(event_count)
 *   ROAS_dN = sum(revenue_dN) / sum(spend)
 *   ROI_dN  = (sum(revenue_dN) - sum(spend)) / sum(spend)
 *   ARPU_dN = sum(revenue_dN) / sum(installs)
 * A zero denominator gives `null` → the cell shows "—", as it always has.
 */

import type { ColumnKey } from "@/lib/columns-config";
import type { EntityTotalsRow } from "@/lib/types";

export type Totals = Record<string, number>;

// Backend field names that come back as plain numbers (counts).
const SUM_INT_FIELDS = [
  "impressions",
  "taps",
  "installs",
  // ASA install / download breakdowns (backend names; column keys
  // installs_tap/view/total are translated inside totalValue).
  "tap_installs",
  "view_installs",
  "total_installs",
  "new_downloads_tap",
  "new_downloads_view",
  "new_downloads_total",
  "redownloads_tap",
  "redownloads_view",
  "redownloads_total",
  // Legacy aggregates (still on the wire for Overview metrics.ts).
  "trials",
  "subscriptions",
  // 18 per-event Adapty native counts.
  "trial_started",
  "trial_converted",
  "trial_renewal_cancelled",
  "trial_renewal_reactivated",
  "trial_expired",
  "subscription_started",
  "subscription_renewed",
  "subscription_renewal_cancelled",
  "subscription_renewal_reactivated",
  "subscription_expired",
  "subscription_paused",
  "subscription_deferred",
  "subscription_refunded",
  "non_subscription_purchase",
  "non_subscription_purchase_refunded",
  "entered_grace_period",
  "billing_issue_detected",
  "access_level_updated",
] as const;

// Backend field names that come back as Decimal-typed strings.
const SUM_DECIMAL_FIELDS = [
  "spend_usd",
  "daily_budget",
  "revenue_usd",
  // 18 per-event revenues.
  "trial_started_revenue",
  "trial_converted_revenue",
  "trial_renewal_cancelled_revenue",
  "trial_renewal_reactivated_revenue",
  "trial_expired_revenue",
  "subscription_started_revenue",
  "subscription_renewed_revenue",
  "subscription_renewal_cancelled_revenue",
  "subscription_renewal_reactivated_revenue",
  "subscription_expired_revenue",
  "subscription_paused_revenue",
  "subscription_deferred_revenue",
  "subscription_refunded_revenue",
  "non_subscription_purchase_revenue",
  "non_subscription_purchase_refunded_revenue",
  "entered_grace_period_revenue",
  "billing_issue_detected_revenue",
  "access_level_updated_revenue",
  // Cohort revenue per window.
  "revenue_d1",
  "revenue_d4",
  "revenue_d7",
  "revenue_d10",
  "revenue_d20",
  "revenue_d30",
  "revenue_d35",
] as const;

const PER_EVENT_KEYS: readonly string[] = [
  "trial_started",
  "trial_converted",
  "trial_renewal_cancelled",
  "trial_renewal_reactivated",
  "trial_expired",
  "subscription_started",
  "subscription_renewed",
  "subscription_renewal_cancelled",
  "subscription_renewal_reactivated",
  "subscription_expired",
  "subscription_paused",
  "subscription_deferred",
  "subscription_refunded",
  "non_subscription_purchase",
  "non_subscription_purchase_refunded",
  "entered_grace_period",
  "billing_issue_detected",
  "access_level_updated",
];

const COHORT_DAYS: readonly number[] = [1, 4, 7, 10, 20, 30, 35];

function num(v: unknown): number {
  const n = typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : 0;
  return Number.isNaN(n) ? 0 : n;
}

/** Legacy fallback: sum the rows actually passed (callers without server
 *  totals). */
export function computeTotals(rows: readonly unknown[]): Totals {
  const t: Totals = {};
  for (const k of SUM_INT_FIELDS) t[k] = 0;
  for (const k of SUM_DECIMAL_FIELDS) t[k] = 0;
  for (const r of rows) {
    const rec = r as Record<string, unknown>;
    for (const k of SUM_INT_FIELDS) {
      const v = rec[k];
      if (typeof v === "number") t[k] += v;
    }
    for (const k of SUM_DECIMAL_FIELDS) t[k] += num(rec[k]);
  }
  return t;
}

/**
 * Lift a server-computed `EntityTotalsRow` (Total filtered, Total by …, or
 * one device's total) into the `Totals` shape `totalValue` reads. Absent
 * fields stay 0 so the formatter renders consistently.
 */
export function totalsFromServer(t: EntityTotalsRow): Totals {
  const out: Totals = {};
  const rec = t as unknown as Record<string, unknown>;
  for (const k of SUM_INT_FIELDS) {
    const v = rec[k];
    out[k] = typeof v === "number" ? v : 0;
  }
  for (const k of SUM_DECIMAL_FIELDS) out[k] = num(rec[k]);
  // Flatten the Adjust totals block: `adj_<key>` for each value, plus
  // installs/cost under reserved keys so totalValue can derive CR/CPA.
  const adj = (rec as { adjust?: Record<string, number | null> | null }).adjust;
  if (adj) {
    out["__adj_installs"] = Number(adj.installs ?? 0);
    out["__adj_cost"] = Number(adj.cost_usd ?? 0);
    for (const [k, v] of Object.entries(adj)) {
      if (k === "installs" || k === "cost_usd") continue;
      out[`adj_${k}`] = Number(v ?? 0);
    }
  }
  return out;
}

export type TotalKind = "money" | "int" | "pct" | "x" | "ipm";
/** A footer cell: its number and how to print it. `null` → "—". IPM keeps
 *  its kind with a null value — the badge draws its own dash. */
export type TotalValue = { kind: TotalKind; v: number | null } | null;

const money = (v: number): TotalValue => ({ kind: "money", v });
const int = (v: number): TotalValue => ({ kind: "int", v });
const pct = (v: number): TotalValue => (Number.isFinite(v) ? { kind: "pct", v } : null);
const times = (v: number): TotalValue => (Number.isFinite(v) ? { kind: "x", v } : null);

/** Every footer column from summed numbers. `null` = no aggregate for the
 *  column (parameters, rule names, dates, share of voice…) or a zero
 *  denominator — both show "—". */
export function totalValue(key: ColumnKey | string, t: Totals): TotalValue {
  // Adjust per-event totals — derived from the flattened adjust block
  // (__adj_installs + adj_<event>* sums). CR = count / installs.
  // CPA = the DISPLAYED (filtered) ASA spend in the Spend column ÷
  // count — NOT the calibrated Adjust cost. The totals/filtered row
  // must derive everything from the filtered set the user sees, so the
  // cost-per-purchase matches the spend shown in the same row.
  if (key.startsWith("adj_")) {
    const adjInstalls = t["__adj_installs"] ?? 0;
    if (key.endsWith("_cr")) {
      const cnt = t[key.slice(0, -3)] ?? 0; // adj_<ev>
      return adjInstalls > 0 ? pct((cnt / adjInstalls) * 100) : null;
    }
    if (key.endsWith("_cpa")) {
      const cnt = t[key.slice(0, -4)] ?? 0; // adj_<ev>
      return cnt > 0 ? money(t.spend_usd / cnt) : null;
    }
    if (key.endsWith("_revenue")) return money(t[key] ?? 0);
    return int(t[key] ?? 0); // bare event count
  }

  // ----- ASA-side performance metrics -----
  if (key === "impressions") return int(t.impressions);
  if (key === "taps") return int(t.taps);
  if (key === "installs") return int(t.installs);
  if (key === "spend") return money(t.spend_usd);
  if (key === "revenue") return money(t.revenue_usd);
  if (key === "daily_budget") return t.daily_budget > 0 ? money(t.daily_budget) : null;
  if (key === "avg_cpt") return t.taps > 0 ? money(t.spend_usd / t.taps) : null;
  if (key === "ttr") return t.impressions > 0 ? pct((t.taps / t.impressions) * 100) : null;
  if (key === "ipm") {
    // Same per-mille formula + colour-coded badge as the data rows.
    return { kind: "ipm", v: t.impressions > 0 ? (t.installs / t.impressions) * 1000 : null };
  }
  if (key === "cpm") return t.impressions > 0 ? money((t.spend_usd / t.impressions) * 1000) : null;
  if (key === "cpa") return t.installs > 0 ? money(t.spend_usd / t.installs) : null;

  // ----- ASA install / download breakdowns (column-key → backend-field) -----
  if (key === "installs_tap") return int(t.tap_installs);
  if (key === "installs_view") return int(t.view_installs);
  if (key === "installs_total") return int(t.total_installs);
  if (key === "new_downloads_tap") return int(t.new_downloads_tap);
  if (key === "new_downloads_view") return int(t.new_downloads_view);
  if (key === "new_downloads_total") return int(t.new_downloads_total);
  if (key === "redownloads_tap") return int(t.redownloads_tap);
  if (key === "redownloads_view") return int(t.redownloads_view);
  if (key === "redownloads_total") return int(t.redownloads_total);
  if (key === "cpa_tap") return t.tap_installs > 0 ? money(t.spend_usd / t.tap_installs) : null;
  if (key === "cpa_view") return t.view_installs > 0 ? money(t.spend_usd / t.view_installs) : null;
  if (key === "cpa_total") return t.total_installs > 0 ? money(t.spend_usd / t.total_installs) : null;
  if (key === "cr_tap") return t.taps > 0 ? pct((t.tap_installs / t.taps) * 100) : null;
  if (key === "cr_total") return t.taps > 0 ? pct((t.total_installs / t.taps) * 100) : null;

  // ----- Per-event count / revenue / CR / CPA totals (18 events × 4) -----
  // Match longer suffixes first so `_revenue` doesn't get consumed as a
  // generic count key.
  for (const ev of PER_EVENT_KEYS) {
    if (key === `${ev}_revenue`) return money(t[`${ev}_revenue`] ?? 0);
    if (key === `${ev}_cr`) {
      return t.installs > 0 ? pct(((t[ev] ?? 0) / t.installs) * 100) : null;
    }
    if (key === `${ev}_cpa`) {
      const cnt = t[ev] ?? 0;
      return cnt > 0 ? money(t.spend_usd / cnt) : null;
    }
    if (key === ev) return int(t[ev] ?? 0);
  }

  // ----- Cohort revenue / ROAS / ROI / ARPU per window -----
  for (const d of COHORT_DAYS) {
    if (key === `revenue_d${d}`) return money(t[`revenue_d${d}`] ?? 0);
    if (key === `roas_d${d}`) {
      const rev = t[`revenue_d${d}`] ?? 0;
      return t.spend_usd > 0 ? times(rev / t.spend_usd) : null;
    }
    if (key === `roi_d${d}`) {
      const rev = t[`revenue_d${d}`] ?? 0;
      return t.spend_usd > 0 ? pct(((rev - t.spend_usd) / t.spend_usd) * 100) : null;
    }
    if (key === `arpu_d${d}`) {
      const rev = t[`revenue_d${d}`] ?? 0;
      return t.installs > 0 ? money(rev / t.installs) : null;
    }
  }

  // Lifetime ROAS column (no _d suffix) — would need a sum of all
  // revenue events, which we no longer track as a single field.
  // Leave as em-dash; users who care about lifetime ROAS can read the
  // per-row value or pick a specific cohort window.
  return null;
}

/** Plain-text rendering of a footer cell (IPM is drawn as a badge by the
 *  table; here it prints its number). */
export function formatTotal(val: TotalValue): string {
  if (val === null || val.v === null) return "—";
  const v = val.v;
  switch (val.kind) {
    case "money":
      return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "int":
      return v.toLocaleString("en-US");
    case "pct":
      return v.toFixed(2) + "%";
    case "x":
      return v.toFixed(2) + "×";
    case "ipm":
      return v.toFixed(2);
  }
}
